import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "react-query";
import Fuse from "fuse.js";
import { CFormInput, CSpinner } from "@coreui/react";
import { useCookie } from "../../../hooks/useCookie";
import apiService from "../../../service/apiService";
import { isNotEmpty, minArraySize } from "../../../helpers/general";

export function mergeClassNames(...classes: any) {
  return classes.filter(Boolean).join(" ");
}

export default function PlaceManagerActivities() {
  const { token } = useCookie("vToken");
  const [searchInput, setSearchInput] = useState<string>("");

  const fetchOffers = async (token: string) => {
    const endpoint = `specials/place-manager/all`;
    return apiService.MakeGetRequest(endpoint, token);
  };

  const queryKey = ["placeManager/getAllOffers", token];

  const { data, isLoading } = useQuery<any, any, MinimalSpecialOffer[], any>(
    queryKey,
    (query) => fetchOffers(query.queryKey[1]),
    {
      keepPreviousData: true,
    },
  );

  const fuse = useMemo(() => {
    if (!data) return null;

    return new Fuse(data, {
      keys: ["title"],
      threshold: 0.4,
      distance: 100,
      ignoreLocation: true,
      useExtendedSearch: false,
      minMatchCharLength: 1,
      shouldSort: true,
      getFn: (obj, path) => {
        const value = Fuse.config.getFn(obj, path);
        if (typeof value === "string") {
          return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        }
        return value;
      },
    });
  }, [data]);

  const filteredOffers = useMemo(() => {
    if (!data) return [];

    if (!searchInput.trim()) {
      return data;
    }

    if (!fuse) return data;

    const normalizedSearch = searchInput
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

    const results = fuse.search(normalizedSearch);
    return results.map((result) => result.item);
  }, [data, searchInput, fuse]);

  if (isLoading) {
    return (
      <section>
        <h5 className="fs-4 fw-bold mb-2">Les activités</h5>
        <div className="pt-4">
          <CSpinner color="danger" />
        </div>
      </section>
    );
  }

  return (
    <section>
      <div className="d-flex justify-content-between items-center">
        <h5 className="fs-4 fw-bold mb-2 flex">Les activités</h5>
        {searchInput && filteredOffers.length > 0 && (
          <div className="text-muted small me-3">
            {filteredOffers.length} résultat{filteredOffers.length > 1 && "s"}{" "}
            trouvé{filteredOffers.length > 1 && "s"}
          </div>
        )}
        <div
          className="position-relative"
          style={{ maxWidth: "300px", width: "300px" }}
        >
          <CFormInput
            type="text"
            placeholder="Rechercher"
            className="custom-input"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {searchInput && (
            <button
              className="btn btn-link position-absolute end-0 top-50 translate-middle-y"
              style={{ padding: "0.25rem 0.5rem" }}
              onClick={() => setSearchInput("")}
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>
      </div>
      <div className="p-4">
        {filteredOffers.length === 0 ? (
          <div className="text-center text-muted py-5">
            <p>Aucune activité trouvée pour "{searchInput}"</p>
          </div>
        ) : (
          <div className="d-flex flex-wrap gap-3">
            {filteredOffers.map((offer) => (
              <OfferCard key={offer.id} offer={offer} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function getVariantSmallestPrice(variants: SpecialOfferVariant[]) {
  return variants.reduce((acc, cur) => {
    return Number(acc.price) < Number(cur.price) ? acc : cur;
  });
}

function getOfferVariantPriceText(offer: MinimalSpecialOffer) {
  if (minArraySize(offer.variants, 1)) {
    if (offer.variants.length === 1) {
      return `MAD ${offer.variants[0].price}`;
    }
    const variantMinPrice = Number(
      getVariantSmallestPrice(offer.variants).price,
    );
    return `À partir de MAD ${variantMinPrice}`;
  }
  return `MAD ${offer.price}`;
}

function getHotelCommission(offer: MinimalSpecialOffer) {
  const percentage = offer.placeCommission ?? 0;
  if (minArraySize(offer.variants, 1)) {
    if (offer.variants.length === 1) {
      const variantPrice = Number(offer.variants[0].price);
      return `MAD ${((variantPrice * percentage) / 100).toFixed(2)}`;
    }
    const variantMinPrice = Number(
      getVariantSmallestPrice(offer.variants).price,
    );
    return `À partir de MAD ${((variantMinPrice * percentage) / 100).toFixed(2)}`;
  }
  return `MAD ${((offer.price * percentage) / 100).toFixed(2)}`;
}

function OfferCard({ offer }: { offer: MinimalSpecialOffer }) {
  // TODO: handle the case for single variant be same as if not variant is given

  return (
    <div
      className="max-w-sm rounded-md overflow-hidden shadow-lg bg-white d-flex flex-col"
      style={{
        minWidth: "350px",
        maxWidth: "350px",
      }}
    >
      {/* Image */}
      <div
        className="relative"
        style={{
          width: "100%",
          height: "180px",
          minHeight: "180px",
          maxHeight: "180px",
          backgroundImage: `url('${offer.carouselPicture}')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* // TODO: add offer details dialog */}
        {/* Badge */}
        {/* <button
          type="button"
          onClick={showDetails}
          className="absolute top-2 right-2 bg-white text-black px-1 py-1 rounded-full text-sm font-medium flex items-center gap-1 border-none"
          style={{
            top: "10px",
            right: "10px",
            border: "none"
          }}
        >
          Details
        </button> */}
      </div>

      {/* Content */}
      <div className="pt-2 ps-3 pe-3 pb-1 flex">
        {/* Title */}
        {isNotEmpty(offer?.offerProvider) ? (
          <p className="text-gray-700 text-sm mb-3">
            Fournie par: {offer.offerProvider}
          </p>
        ) : null}
        <h3 className="font-bold text-lg mb-2 text-gray-800">{offer.title}</h3>

        {/* Description */}
        {/* // TODO: get this info from the offer details */}
        <p className="text-gray-600 text-sm mb-2">Surf • 2h</p>

        {/* Price */}
        <div className="d-flex items-center gap-2">
          <span className="text-2xl font-bold text-gray-900">
            {getOfferVariantPriceText(offer)}
          </span>
        </div>
        <div className="d-flex flex-col items-start">
          <div className="d-flex justify-content-end gap-2 flex-wrap">
            <span className="text-sm text-gray-400">
              Commission hôtelière ({offer.placeCommission}%):
            </span>
            <span className="text-sm font-medium text-gray-400">
              {getHotelCommission(offer)}
            </span>
          </div>
        </div>
      </div>

      <div className="w-full d-flex justify-content-end mt-2">
        <Link
          to={`/place-manager/activities/booking/${offer.id}`}
          className="rounded-sm py-2 px-5 mx-2 mb-2 d-flex align-items-center gap-2 justify-content-center shadow-primary border-0 text-primary flex-shrink-0 bg-gradient-primary text-white"
        >
          Réserver
        </Link>
      </div>
    </div>
  );
}
